import { getCuisineNames } from "../model/cuisineTypes";
import { Diet_Types } from "../model/dietTypes";
import { Difficulty_Levels } from "../model/difficultyLevels";
import { SCORE_THRESHOLDS } from "../model/constants";
import {
  DietType,
  FoodType,
  PlanMealItemCommonType,
  RecipeItemType,
  RecipeType,
} from "../model/nutritionPlanTypes";
import { User_Friendly_Units } from "../model/userFriendlyUnits";

/**
 * Presentation helpers for meal cards and details, ported from the mobile
 * `components/daily/mealItemHelpers.ts`. One deliberate change: `getScoreLevel`
 * returns the band only — colours come from the theme at the call site.
 */

export type ScoreLevel = "Low" | "Moderate" | "High";

/** "HH:MM:SS" → "30 min", "1h 30min", "1h"; anything unparseable → "0 min". */
export const formatPrepTime = (duration: string | undefined | null): string => {
  if (!duration) return "0 min";
  const parts = duration.split(":");
  if (parts.length !== 3) return "0 min";
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return "0 min";
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  if (m > 0) return `${m} min`;
  return "0 min";
};

/** 1–3 → Easy / Medium / Hard; unknown → Medium. */
export const formatDifficulty = (level: number | undefined): string =>
  (level && Difficulty_Levels[level]) || "Medium";

/** One cuisine tag (without the " kitchen" suffix) and up to two diet tags. */
export const extractContentTags = (
  content: FoodType | RecipeType
): { cuisineTag: string | null; dietTags: string[] } => {
  let cuisineTag: string | null = null;
  if (content.cuisine) {
    const names = getCuisineNames(content.cuisine);
    if (names.length > 0) cuisineTag = names[0].replace(" kitchen", "");
  }

  const dietTags: string[] = [];
  if (Array.isArray(content.diet_types)) {
    content.diet_types.slice(0, 2).forEach((dietType: DietType) => {
      const name = Diet_Types[dietType.diet_type]?.name;
      if (name) dietTags.push(name);
    });
  }

  return { cuisineTag, dietTags };
};

/** The first `maxCount` ingredient names by priority of addition, then by servings. */
export const getMainIngredients = (
  recipeItems: RecipeItemType[] | undefined,
  maxCount = 2
): string[] => {
  if (!recipeItems || recipeItems.length === 0) return [];
  return recipeItems
    .filter((item) => item.food !== null && item.food !== undefined)
    .sort((a, b) => {
      if (
        a.priority_of_addition !== undefined &&
        b.priority_of_addition !== undefined
      ) {
        return a.priority_of_addition - b.priority_of_addition;
      }
      return (
        (b.food?.number_of_servings || 0) - (a.food?.number_of_servings || 0)
      );
    })
    .slice(0, maxCount)
    .map((item) => item.food!.name)
    .filter((name) => !!name);
};

/** "1 Cup", "2 portions" — the serving label for a food. */
export const getPortionInfo = (food: FoodType | null | undefined): string => {
  if (!food) return "1 portion";
  const servings = food.number_of_servings || 1;
  const selectedUnit = food.user_friendly_units?.find(
    (unit) => unit.id === food.selected_user_friendly_unit_id
  );
  const label =
    selectedUnit && User_Friendly_Units[selectedUnit.user_friendly_unit]?.label;
  if (label) return `${servings} ${label}`;
  return `${servings} portion${servings !== 1 ? "s" : ""}`;
};

/** Recipe macros summed over its ingredients, to one decimal. */
export const calculateRecipeMacros = (
  recipe: RecipeType | null | undefined
): { protein: number; fat: number; carbs: number } => {
  if (!recipe?.items) return { protein: 0, fat: 0, carbs: 0 };
  const totals = recipe.items.reduce(
    (acc, item) => {
      if (item.food) {
        acc.protein += item.food.protein || 0;
        acc.fat += item.food.fat || 0;
        acc.carbs += item.food.carbs || 0;
      }
      return acc;
    },
    { protein: 0, fat: 0, carbs: 0 }
  );
  return {
    protein: Math.round(totals.protein * 10) / 10,
    fat: Math.round(totals.fat * 10) / 10,
    carbs: Math.round(totals.carbs * 10) / 10,
  };
};

/** Low 0–35, Moderate 36–59, High 60+. */
export const getScoreLevel = (score: number): ScoreLevel => {
  if (score <= SCORE_THRESHOLDS.lowMax) return "Low";
  if (score <= SCORE_THRESHOLDS.moderateMax) return "Moderate";
  return "High";
};

/** Average of the ingredient scores, to two decimals; null when none has one. */
export const calculateRecipeScore = (recipe: RecipeType): number | null => {
  const scores = (recipe.items ?? [])
    .map((item) => item.food?.food_score?.total_score)
    .filter((score): score is number => !!score);
  if (scores.length === 0) return null;
  const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return Math.round(average * 100) / 100;
};

/** A food's own score, or a recipe's averaged score; null when unavailable. */
export const getMealItemScore = (
  mealItem: PlanMealItemCommonType
): number | null => {
  if (mealItem.food?.food_score?.total_score) {
    return mealItem.food.food_score.total_score;
  }
  if (mealItem.recipe) return calculateRecipeScore(mealItem.recipe);
  return null;
};
